// src/slices/claimDossier/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PrizeClaimDossier } from 'pages/ClaimDossier/types';
import {
  fetchClaimDossiers,
  fetchClaimDossierById,
  createClaimDossier,
  approveClaimDossier,
  cancelClaimDossier,
  payClaimDossier,
} from './thunk';

interface ModalState {
  create: boolean;
  detail: boolean;
  cancel: boolean;
}

interface ClaimDossierState {
  dossiers: PrizeClaimDossier[];
  pagination: { pageIndex: number; pageSize: number; totalCount: number; totalPages: number };
  filters: { status?: string };
  selectedDossier: PrizeClaimDossier | null;
  loading: boolean;
  detailLoading: boolean;
  submitting: boolean;
  // Id hồ sơ đang được ký duyệt/hủy — null khi không có thao tác nào đang chạy. Tách riêng theo dòng (thay vì
  // 1 flag chung) vì bảng danh sách hiển thị nhiều hồ sơ PendingApproval cùng lúc, chỉ nút của đúng dòng đang
  // xử lý mới cần hiện loading.
  approvingId: number | null;
  cancellingId: number | null;
  // Id hồ sơ đang được chuyển khoản (màn Kế toán Chi trả) — cùng pattern approvingId/cancellingId.
  payingId: number | null;
  error: string | null;
  modalState: ModalState;
}

const initialState: ClaimDossierState = {
  dossiers: [],
  pagination: { pageIndex: 1, pageSize: 20, totalCount: 0, totalPages: 0 },
  filters: {},
  selectedDossier: null,
  loading: false,
  detailLoading: false,
  submitting: false,
  approvingId: null,
  cancellingId: null,
  payingId: null,
  error: null,
  modalState: { create: false, detail: false, cancel: false },
};

const claimDossierSlice = createSlice({
  name: 'ClaimDossier',
  initialState,
  reducers: {
    setStatusFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filters.status = action.payload;
      state.pagination.pageIndex = 1;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pagination.pageIndex = action.payload;
    },
    setSelectedDossier: (state, action: PayloadAction<PrizeClaimDossier | null>) => {
      state.selectedDossier = action.payload;
    },
    openModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClaimDossiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClaimDossiers.fulfilled, (state, action) => {
        state.loading = false;
        const payload: any = action.payload;
        state.dossiers = payload?.items || [];
        state.pagination = {
          pageIndex: payload?.pageIndex ?? 1,
          pageSize: payload?.pageSize ?? 20,
          totalCount: payload?.totalCount ?? 0,
          totalPages: payload?.totalPages ?? 0,
        };
      })
      .addCase(fetchClaimDossiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchClaimDossierById.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchClaimDossierById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedDossier = action.payload as any;
      })
      .addCase(fetchClaimDossierById.rejected, (state) => {
        state.detailLoading = false;
      })

      .addCase(createClaimDossier.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createClaimDossier.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.create = false;
      })
      .addCase(createClaimDossier.rejected, (state) => {
        state.submitting = false;
      })

      .addCase(approveClaimDossier.pending, (state, action) => {
        state.approvingId = action.meta.arg;
      })
      .addCase(approveClaimDossier.fulfilled, (state) => {
        state.approvingId = null;
      })
      .addCase(approveClaimDossier.rejected, (state) => {
        state.approvingId = null;
      })

      .addCase(cancelClaimDossier.pending, (state, action) => {
        state.cancellingId = action.meta.arg.id;
      })
      .addCase(cancelClaimDossier.fulfilled, (state) => {
        state.cancellingId = null;
        state.modalState.cancel = false;
      })
      .addCase(cancelClaimDossier.rejected, (state) => {
        state.cancellingId = null;
      })

      .addCase(payClaimDossier.pending, (state, action) => {
        state.payingId = action.meta.arg;
      })
      .addCase(payClaimDossier.fulfilled, (state) => {
        state.payingId = null;
      })
      .addCase(payClaimDossier.rejected, (state) => {
        state.payingId = null;
      });
  },
});

export const { setStatusFilter, setPageIndex, setSelectedDossier, openModal, closeModal } = claimDossierSlice.actions;
export default claimDossierSlice.reducer;
